<template>
  <provider-cost-estimator
    ref="providerCostEstimator"
    v-if="showCharges"
    :industryAverageCharge="industryAverageCharge"
    upperText="Use the slider to compare your current providers total annual charges if you know this, or leave it as 1%, as that is a typical average total charges figure."
    lowerText="Don't know your current providers charges as they are not clear? Maybe you should ask them why they aren't!"
    @charge-rate-changed="chargeRateChanged">
  </provider-cost-estimator>

  <sub-section :title="title" :showHelp="false">
      <div class="container-fluid">
          <div class="header-row d-none d-sm-flex row pt-3 row-divider">
              <div class="d-block col-md-2 ps-0"><p class="text-start fw-bold px-0 mx-0">Provider</p></div>
              <template v-if="showCharges">
                  <div class="d-block col-md-2 pt-md-0 px-0 ps-md-4">
                      <p class="text-end fw-bold px-0 mx-0">Charges<sup>*</sup></p>
                  </div>
                  <div class="d-block col-md-2 pt-md-0 px-0 ps-md-4">
                      <p class="text-end fw-bold px-0 mx-0">Savings Possible</p>
                  </div>
              </template>
              <div class="d-block pt-md-0 px-0 ps-md-4" :class="showCharges ? 'col-md-5' : 'col-md-9'"><p class="text-start fw-bold px-0 mx-0">Best suited to?</p></div>
              <div class="d-block col-md-1 pe-0"></div>
          </div>

          <div v-for="provider in providers" :key="provider.key" class="row pt-3 row-divider">
              <div class="d-block col-md-2 ps-0 mb-4">
                  <img :src="provider.image" class="w-100" :alt="provider.name" />
              </div>
              <template v-if="showCharges">
                <div class="d-sm-none">
                  <div class="row">
                    <div class="col-7 px-0 text-start">
                      <p class="fw-bold">Lifetime Charges <sup>*</sup></p>
                    </div>
                    <div class="col-5 text-end">
                      <p>{{ formatter.formatCurrency(provider.charge) }}</p>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-7 px-0 text-start">
                      <p class="fw-bold">Savings Possible</p>
                    </div>
                    <div class="col-5 text-end">
                      <p><span :class="provider.saving >= 0 ? 'text-success' : 'text-danger'">{{ formatter.formatCurrency(provider.saving) }}</span></p>
                    </div>
                  </div>
                </div>

                <div class="d-none d-sm-block col-md-2 pt-md-0 px-0 ps-md-4">
                  <p class="text-end">{{ formatter.formatCurrency(provider.charge) }}</p>
                </div>
                <div class="d-none d-sm-block col-md-2 pt-md-0 px-0 ps-md-4">
                  <p class="text-end"><span :class="provider.saving >= 0 ? 'text-success' : 'text-danger'">{{ formatter.formatCurrency(provider.saving) }}</span></p>
                </div>
              </template>
              <div class="d-block pt-md-0 px-0 ps-md-4" :class="showCharges ? 'col-md-6' : 'col-md-9'">
                  <p class="text-start">{{ provider.description }}</p>
                  <p class="d-none d-md-block text-start"><a href="#" @click="onVisitClicked(provider.key)">Visit our website</a></p>
              </div>
              <div class="d-grid d-md-none col-md-1 pe-0">
                  <button class="btn btn-primary btn-sm floatend m-0 mb-3" @click="onVisitClicked(provider.key)">Visit</button>
              </div>
          </div>
    </div>

    <div v-if="showCostComparisonAlert" class="alert alert-info mt-2 mb-0" role="alert">
      <h5 class="alert-heading">
        <i class="bi bi-info-circle text-muted fs-5 me-2"></i>Compare your costs
      </h5>
      <p class="mb-0">
        You can compare any provider's expected costs in £ terms based on your plan against 
        your current provider in the provider page on our <a href="guiideplus">Dashboard</a>.
      </p>
    </div>
  </sub-section>

  <template v-if="showCharges">
    <p class="text-start">
      <sup>*</sup>
      <small>
        Charges are estimated based on your Guiide plan and are in respect of charges from your retirement date only. We have not considered any charges or possible savings before retirement. The charges are;
      </small>
    </p>
    <p v-for="provider in providers" :key="provider.key" class="text-start">
      <small>{{ provider.subscript }}</small>
    </p>
  </template>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import ProviderCostEstimator from "@/guiide/components/guiideplus/providers/ProviderCostEstimator.vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import { formatter } from "@/shared/ts/formatter";
import { ProviderCharges } from "../../ts/providerCharges";
import { app } from "../../ts/app";
import Monitoring from "@/guiide/ts/monitoring";
import { useStore } from "vuex";

const store = useStore();

type ProviderInfo = { key: string, name: string, abbrevName: string, description: string[], subscript: string, charge: number, saving: number, image: string, url: string, eventName: string };

const props = defineProps({
    showCharges: { type: Boolean, default: true },
    title: { type: String, default: "Partners" },
    showCostComparisonAlert: { type: Boolean, default: false }
});

const providerCostEstimator = ref(null);
const providerCharges = ref<ProviderCharges|null>(null);
const providers = ref(new Array<ProviderInfo>());
const pensionProvidersInStore = computed(() => store.state.pensionProvidersForFindYourself);

onMounted(async () => {
  if (store.state.pensionProvidersForFindYourself == null || store.state.pensionProvidersForFindYourself.length == 0) {
      app.getPensionProviders();
  }

  if (props.showCharges) {
    providerCharges.value = await app.getProviderCharges();
  }
});

watch(
  [pensionProvidersInStore, providerCharges],
  ([newProviders, providerCharges]) => {
    if (newProviders.length == 0 || (props.showCharges && providerCharges == null)) {
      return;
    }

    for (const provider of newProviders) {
      let charge = { orgKey: "", amount: 0 };
      
      if (props.showCharges) {
        charge = providerCharges.charges.find(c => c.orgKey == provider.key);
      }

      const eventName = `LowCost${provider.key}`;

      providers.value.push(createProvider(
        provider.key,
        provider.name,
        provider.name,
        provider.description,
        provider.chargeNotePersonalComparison,
        charge.amount,
        provider.image,
        provider.url,
        eventName
      ));

      if (props.showCharges) {
        providerCostEstimator.value.invokeChangeEvent();
      }
    }
  }
);

const industryAverageCharge = computed<number>(() => providerCharges.value?.industryAverage ?? 0 );

function chargeRateChanged(chargeRate: number, cost: number) {
  for (const provider of providers.value) {
    provider.saving = getProviderSaving(provider.charge, cost);
  }
}

function createProvider(key: string, name: string, abbrevName: string, description: string[], subscript: string, charge: number, image: string, url: string, eventName: string): ProviderInfo {
  return {
    key: key,
    name: name,
    abbrevName: abbrevName,
    description: description,
    subscript: subscript,
    charge: charge,
    saving: 0,
    image: image,
    url: url,
    eventName: eventName
  };
}

function getProviderSaving(
      providerCost: null | number,
      industryCost: null | number
    ): null | number {
  if (providerCost == null || industryCost == null) {
    return null;
  }

  const saving = industryCost - providerCost;
  return saving;
}

function onVisitClicked(providerKey: string) {
  const provider = providers.value.find(p => p.key == providerKey);
  const potValue = store.state.retiree.pensionPots.presentValue;
  Monitoring.gaEvent(provider.eventName, potValue);
  window.open(provider.url, "_blank");
}
</script>

<style scoped>
.row-divider:not(:last-of-type) {
    border-bottom: 1px solid lightgray;
    margin-bottom: 0.3rem;
    padding-bottom: 0.3rem;
}

.header-row p {
  margin-bottom: 0;
}
</style>
